/**
 * Utilitaire partagé pour la vérification des horaires d'ouverture téléphonique
 * Utilisé par : meetings-and-callme.js, mobile-header-topbar.js
 */
window.PhoneStatusUtils = (function () {
  const WEEKDAY_ARR = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  const DEFAULT_OPENING_HOURS = {
    monday: { start: 9, end: 19 },
    tuesday: { start: 9, end: 19 },
    wednesday: { start: 9, end: 19 },
    thursday: { start: 9, end: 19 },
    friday: { start: 9, end: 19 },
    saturday: { start: 9, end: 19 },
    sunday: null,
  };

  /**
   * Vérifie si la ligne téléphonique est ouverte selon l'heure de Paris
   * @param {Object} openingHours - Horaires d'ouverture (optionnel, utilise les defaults sinon)
   * @returns {boolean}
   */
  function isPhoneLineOpen(openingHours = DEFAULT_OPENING_HOURS) {
    const now = new Date();
    const options = { timeZone: 'Europe/Paris', hour12: false, hour: '2-digit', minute: '2-digit' };
    const parisTimeParts = new Intl.DateTimeFormat('fr-FR', options).formatToParts(now);
    const hourPart = parisTimeParts.find((part) => part.type === 'hour');
    const currentHour = hourPart ? parseInt(hourPart.value, 10) : 0;
    const minutePart = parisTimeParts.find((part) => part.type === 'minute');
    const currentMinute = minutePart ? parseInt(minutePart.value, 10) : 0;
    // Heure décimale (ex. 9h30 -> 9.5) pour comparer aux horaires à la demi-heure.
    const currentTime = currentHour + currentMinute / 60;

    const day = WEEKDAY_ARR[now.getUTCDay()];
    const openingHoursToday = openingHours[day];

    if (openingHoursToday) {
      return currentTime >= openingHoursToday.start && currentTime < openingHoursToday.end;
    }

    return false;
  }

  /**
   * Parse les horaires depuis un attribut data-* JSON
   * @param {string|null} jsonString - JSON string des horaires
   * @returns {Object} - Horaires parsés ou defaults
   */
  function parseOpeningHours(jsonString) {
    if (!jsonString) return DEFAULT_OPENING_HOURS;
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.warn('[PhoneStatusUtils] Invalid opening hours JSON, using defaults');
      return DEFAULT_OPENING_HOURS;
    }
  }

  /**
   * Crée un gestionnaire de statut pour un élément DOM
   * @param {Object} config
   * @param {HTMLElement} config.element - Élément à surveiller (pour lire data-phone-opening-hours)
   * @param {HTMLElement} config.statusElement - Élément dont les classes seront modifiées
   * @param {string} config.availableClass - Classe CSS pour "disponible"
   * @param {string} config.unavailableClass - Classe CSS pour "indisponible"
   * @param {Function} config.onStatusChange - Callback optionnel appelé lors d'un changement
   * @returns {Object} - Gestionnaire avec méthodes start(), stop(), check()
   */
  function createStatusManager(config) {
    const { element, statusElement, availableClass, unavailableClass, onStatusChange } = config;

    const openingHours = parseOpeningHours(element?.dataset?.phoneOpeningHours);
    let lastStatus = null;
    let intervalId = null;

    function check() {
      if (!statusElement) return;

      const isOpen = isPhoneLineOpen(openingHours);

      if (isOpen !== lastStatus) {
        if (isOpen) {
          statusElement.classList.add(availableClass);
          statusElement.classList.remove(unavailableClass);
        } else {
          statusElement.classList.add(unavailableClass);
          statusElement.classList.remove(availableClass);
        }
        lastStatus = isOpen;

        if (typeof onStatusChange === 'function') {
          onStatusChange(isOpen);
        }
      }
    }

    function start() {
      check();
      intervalId = setInterval(check, 60000);
    }

    function stop() {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    }

    return { start, stop, check, isOpen: () => isPhoneLineOpen(openingHours) };
  }

  // API publique
  return {
    isPhoneLineOpen,
    parseOpeningHours,
    createStatusManager,
    DEFAULT_OPENING_HOURS,
  };
})();
